import { twMerge } from "tailwind-merge";
import {
  SignupFormBase,
  type SignupFormBaseProps,
  type SignupFormData,
} from "@core/features/auth";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { AuthPasswordRequirementsContainer } from "@themes/pollinator/ui/AuthPasswordRequirements";
import { AuthSignupFormAdditionalContent } from "../../components/AuthSignupFormAdditionalContent";
import { AuthButtons } from "@themes/pollinator/ui/AuthButtons";

export type { SignupFormData };

export type Props = Omit<
  SignupFormBaseProps,
  | "renderForm"
  | "renderActions"
  | "renderAdditionalContent"
  | "onSignup"
  | "onSignupWithGoogle"
  | "hideLoginLink"
> & {
  /** Handler for form submission */
  onSignup: () => Promise<void>;
  /** Handler for Google signup */
  onSignupWithGoogle: () => Promise<void>;
  /** Whether to show additional content (terms, etc.) */
  isWithAdditionalContent?: boolean;
};

/**
 * Signup form component that renders registration fields with submit actions.
 * This is a presentational component - business logic should be in the container.
 */
export function SignupForm({
  form,
  className,
  isWithAdditionalContent = true,
  onSignup,
  onSignupWithGoogle,
}: Props) {
  return (
    <SignupFormBase
      form={form}
      onSignup={onSignup}
      onSignupWithGoogle={onSignupWithGoogle}
      className={"flex-1"}
      renderForm={({ form }) => (
        <Form
          form={form}
          className={twMerge("px-8 py-8 flex-1", className)}
          innerClassName="justify-center"
        >
          <FormField
            name="firstName"
            label="First Name"
            component="Input"
            placeholder="Enter your first name"
            required
          />
          <FormField
            name="lastName"
            label="Last Name"
            component="Input"
            placeholder="Enter your last name"
            required
          />
          <FormField
            name="email"
            label="Email"
            component="Input"
            placeholder="Enter your email"
            autoComplete="username"
            required
          />
          <FormField
            name="confirmEmail"
            label="Email Confirmation"
            component="Input"
            placeholder="Confirm your email"
            required
          />
          <FormField
            name="phoneNumber"
            label="Phone Number"
            component="PhoneInput"
            placeholder="Enter your phone number"
          />
          <FormField
            name="confirmPhoneNumber"
            label="Phone Number Confirmation"
            component="PhoneInput"
            placeholder="Confirm your phone number"
          />
          <FormField
            name="password"
            label="Password"
            component="Input"
            type="password"
            placeholder="Enter your password"
            autoComplete="new-password"
            required
            isHideErrorMessage
          />
          <FormField
            name="confirmPassword"
            label="Password Confirmation"
            component="Input"
            type="password"
            placeholder="Confirm your password"
            autoComplete="new-password"
            required
          />
          <AuthPasswordRequirementsContainer />
          {isWithAdditionalContent && <AuthSignupFormAdditionalContent />}
        </Form>
      )}
      renderActions={({
        isSubmitting,
        isValid,
        onSignup,
        onSignupWithGoogle,
      }) => (
        <AuthButtons
          className={className}
          isSubmitting={isSubmitting}
          isLoading={false}
          isValid={isValid}
          onSubmit={onSignup}
          onSubmitWithGoogle={onSignupWithGoogle}
          submitLabel="Sign Up"
          googleLabel="Sign Up with Google"
          googleButtonClassName="w-full"
          signupButtonClassName="w-full"
        />
      )}
    />
  );
}

export default SignupForm;

